import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaUsers,
  FaChalkboardTeacher,
  FaCalendarAlt,
  FaBell,
  FaCheckCircle,
  FaTimesCircle,
  FaFileAlt,
  FaEdit,
  FaTrash,
  FaEye,
  FaPlus,
  FaLink,
  FaBook,
  FaClipboardList,
  FaGoogle,
  FaVideo,
} from "react-icons/fa";
import "./AdminDashboard.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import GoogleClassroom from "../../components/GoogleClassroom";

// Configure axios globally
axios.defaults.withCredentials = true;

// Add response interceptor for debugging
axios.interceptors.response.use(
  (response) => {
    console.log("API Response:", response.config.url, response.status);
    return response;
  },
  (error) => {
    console.error(
      "API Error:",
      error.config?.url,
      error.response?.status,
      error.message,
    );
    return Promise.reject(error);
  },
);

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [pendingStudents, setPendingStudents] = useState([]);
  const [studentApplications, setStudentApplications] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [teacherForm, setTeacherForm] = useState({});
  const [studentForm, setStudentForm] = useState({});
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [showTeacherModal, setShowTeacherModal] = useState(false);
  const [showEditTeacherModal, setShowEditTeacherModal] = useState(false);
  const [showEditStudentModal, setShowEditStudentModal] = useState(false);
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    pendingApprovals: 0,
    activeClasses: 0,
  });
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [googleCourses, setGoogleCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseDetails, setCourseDetails] = useState(null);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [googleAuthenticated, setGoogleAuthenticated] = useState(false);
  // Add this with other state declarations (around line 30-50)
const [admissionDetails, setAdmissionDetails] = useState([]);
const [loadingAdmissions, setLoadingAdmissions] = useState(false);
const [selectedAdmission, setSelectedAdmission] = useState(null);
const [showAdmissionModal, setShowAdmissionModal] = useState(false);
// Add with other state declarations
const [leaveApplications, setLeaveApplications] = useState([]);
const [loadingLeaves, setLoadingLeaves] = useState(false);
const [selectedLeave, setSelectedLeave] = useState(null);
const [showLeaveModal, setShowLeaveModal] = useState(false);
const [leaveReviewComments, setLeaveReviewComments] = useState("");
const [leaveReviewStatus, setLeaveReviewStatus] = useState("approved");

  const [newStudentForm, setNewStudentForm] = useState({
    fullName: "",
    fatherName: "",
    email: "",
    password: "",
    contact: "",
    altContact: "",
    dob: "",
    address: "",
    programId: "",
    cohortId: "",
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);


  const [programs, setPrograms] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [activeCohorts, setActiveCohorts] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [cohortForm, setCohortForm] = useState({
    programId: "",
    startMonth: "",
    startYear: "",
  });

  const [assignForm, setAssignForm] = useState({
    programId: "",
    cohortId: "",
    subjectId: "",
    teacherId: "",
  });
  const [assignedSubjects, setAssignedSubjects] = useState([]);

  const [batchDetails, setBatchDetails] = useState([]);
  const [loadingBatchDetails, setLoadingBatchDetails] = useState(false);
  // Add with other state declarations (around line 30-50)
  const [batchFilters, setBatchFilters] = useState({
    programId: "",
    cohortId: "",
  });
  const [filteredCohorts, setFilteredCohorts] = useState([]);

  // NEW: Classroom Management States

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Add to your useEffect for checking Google auth status
  useEffect(() => {
    checkGoogleAuthStatus();
  }, []);

 const checkGoogleAuthStatus = async () => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/google-classroom/auth-status`
    );

    const isAuth = response.data.authenticated || false;
    setGoogleAuthenticated(isAuth);

    if (isAuth) {
      fetchGoogleCourses();
    }
  } catch (error) {
    setGoogleAuthenticated(false);
  }
};

// Helper function to generate temporary password
const generateTemporaryPassword = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let password = "";
  for (let i = 0; i < 8; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};


  useEffect(() => {
    const loadAllAssignments = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/teacher/subject-teacher`,
        );
        setAssignedSubjects(res.data || []);
      } catch (err) {
        console.error("Failed to load assignments", err);
      }
    };

    loadAllAssignments();
  }, []);

  useEffect(() => {
    if (!newStudentForm.programId) {
      setActiveCohorts([]);
      return;
    }

    const filtered = cohorts.filter(
      (c) => c.programId?._id === newStudentForm.programId,
    );

    setActiveCohorts(filtered);
  }, [newStudentForm.programId, cohorts]);

  useEffect(() => {
    if (showTeacherModal || showEditTeacherModal || showEditStudentModal) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [showTeacherModal, showEditTeacherModal, showEditStudentModal]);

  // Add this useEffect to check for Google auth success in URL
  // useEffect(() => {
   
  //   const urlParams = new URLSearchParams(window.location.search);
  //   const googleAuth = urlParams.get("googleAuth");

  //   if (googleAuth === "success") {
  //     toast.success("Google Classroom connected successfully!");
    
  //     window.history.replaceState({}, document.title, window.location.pathname);
    
  //     checkGoogleAuthStatus();
  //   }
  // }, []);

  // Add with other useEffect functions
useEffect(() => {
  if (activeTab === "leaveApplications") {
    fetchLeaveApplications();
  }
}, [activeTab]);

const fetchLeaveApplications = async () => {
  try {
    setLoadingLeaves(true);
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/leaves/applications`
    );
    
    if (response.data.success) {
      setLeaveApplications(response.data.leaves || []);
    } else {
      toast.error("Failed to fetch leave applications");
    }
  } catch (error) {
    console.error("Error fetching leave applications:", error);
    toast.error("Failed to load leave applications");
  } finally {
    setLoadingLeaves(false);
  }
};

  const fetchDashboardData = async () => {
    try {
      const pendingRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/pending-students`,
      );
      setPendingStudents(pendingRes.data);

      const applicationsRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/student-applications`,
      );
      setStudentApplications(applicationsRes.data);

      const statsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/admin/stats`);
      setStats(statsRes.data);

      // Fetch classes with populated data

      const allStuRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/students`,
      );
      setAllStudents(allStuRes.data);

      const teachersRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/teachers`,
      );
      setTeachers(teachersRes.data);

      const programRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/programs`);
      setPrograms(programRes.data);

      const cohortRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/cohorts`);
      setCohorts(cohortRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    }
  };

  // Add this with other useEffect functions
useEffect(() => {
  if (activeTab === "admissionDetails") {
    fetchAdmissionDetails();
  }
}, [activeTab]);

const fetchAdmissionDetails = async () => {
  try {
    setLoadingAdmissions(true);
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admission/admission-details`
    );
    
    if (response.data.success) {
      setAdmissionDetails(response.data.admissions || []);
    } else {
      toast.error("Failed to fetch admission details");
    }
  } catch (error) {
    console.error("Error fetching admission details:", error);
    toast.error("Failed to load admission details");
  } finally {
    setLoadingAdmissions(false);
  }
};

  // Existing handlers (unchanged)
  const handleTeacherChange = useCallback((e) => {
    setTeacherForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }, []);

const handleCreateStudent = async (e) => {
  e.preventDefault();

  try {
    // ============================================
    // VALIDATION (EXISTING - NO CHANGES)
    // ============================================
    const validationErrors = [];

    // Required fields check
    if (!newStudentForm.fullName?.trim()) {
      validationErrors.push("Full name is required");
    }

    if (!newStudentForm.email?.trim()) {
      validationErrors.push("Email is required");
    } else {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newStudentForm.email.trim())) {
        validationErrors.push("Please enter a valid email address");
      }
    }

    if (!newStudentForm.contact?.trim()) {
      validationErrors.push("Contact number is required");
    } else if (newStudentForm.contact.trim().length < 10) {
      validationErrors.push("Contact number must be at least 10 digits");
    }

    if (!newStudentForm.programId) {
      validationErrors.push("Please select a program");
    }

    // Check if program is selected before requiring cohort
    if (newStudentForm.programId && !newStudentForm.cohortId) {
      validationErrors.push("Please select a cohort for the program");
    }

    // Show validation errors
    if (validationErrors.length > 0) {
      validationErrors.forEach(error => toast.error(error));
      return;
    }

    // ============================================
    // PREPARE DATA (EXISTING - NO CHANGES)
    // ============================================
    
    // Create clean data object with only necessary fields
    const studentData = {
      // Required fields from User model
      fullName: newStudentForm.fullName.trim(),
      fatherName: newStudentForm.fatherName?.trim() || "",
      email: newStudentForm.email.trim().toLowerCase(),
      contact: newStudentForm.contact.trim(),
      
      // Optional fields
      altContact: newStudentForm.altContact?.trim() || "",
      dob: newStudentForm.dob || "",
      address: newStudentForm.address?.trim() || "",
      qualifications: newStudentForm.qualifications?.trim() || "",
      experience: newStudentForm.experience?.trim() || "",
      
      // Program and cohort
      programId: newStudentForm.programId,
      cohortId: newStudentForm.cohortId || null,
      
      // Role is automatically set to "student" in backend
      // Password is optional - backend will generate one
      ...(newStudentForm.password && { password: newStudentForm.password }),
    };

    console.log("Sending student data:", {
      ...studentData,
      password: newStudentForm.password ? "[PROVIDED]" : "[WILL BE GENERATED]"
    });

    // ============================================
    // API CALL (EXISTING - NO CHANGES)
    // ============================================
   

    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/create-student`,
      studentData,
      {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      }
    );

    // ============================================
    // CHECK EMAIL STATUS (NEW - ADDED)
    // ============================================
    
    // Check if email was sent successfully
    let emailStatusMessage = "";
    if (response.data.emailSent) {
      emailStatusMessage = `✓ Credentials have been emailed to ${studentData.email}`;
      toast.success("Student created and credentials emailed!", { id: "create-student" });
    } else {
      emailStatusMessage = `⚠ Student created but email could not be sent to ${studentData.email}`;
      toast.success("Student created successfully!", { id: "create-student" });
      toast.warning(emailStatusMessage, { autoClose: 5000 });
    }

    // ============================================
    // SHOW CREDENTIALS TO USER (UPDATED)
    // ============================================
    if (response.data.credentials) {
      const { enrollmentId, email, password } = response.data.credentials;
      
      // Create a message with credentials and email status
      const credentialMessage = `
        ${emailStatusMessage}
        
        ✅ Enrollment ID: ${enrollmentId}
        📧 Email: ${email}
        🔑 Password: ${password}
        
        ${response.data.emailSent ? 
          'The student will receive these credentials via email.' : 
          'Please manually provide these credentials to the student.'}
        
        Student should change password on first login.
      `;
      
      // Show credentials in a toast
      toast.info(credentialMessage, {
        duration: 10000, // Show for 10 seconds
        position: "top-center"
      });

      // Also show in alert for better visibility
      alert(`
        STUDENT ACCOUNT CREATED
        ========================
        
        ${emailStatusMessage}
        
        STUDENT CREDENTIALS:
        ---------------------
        Enrollment ID: ${enrollmentId}
        Email: ${email}
        Password: ${password}
        
        ${response.data.emailSent ? 
          '✓ Credentials sent to student\'s email.' : 
          '⚠ Please provide credentials manually.'}
        
        IMPORTANT:
        • Student should change password on first login
        • Save these credentials securely
      `);
    }

    // ============================================
    // RESET FORM AND UPDATE UI (EXISTING - NO CHANGES)
    // ============================================
    setShowAddStudentModal(false);
    setNewStudentForm({
      fullName: "",
      fatherName: "",
      email: "",
      password: "", // Clear password field
      contact: "",
      altContact: "",
      dob: "",
      address: "",
      qualifications: "",
      experience: "",
      programId: "",
      cohortId: "",
    });

    // Refresh dashboard data
    fetchDashboardData();

  } catch (err) {
    console.error("Error creating student:", {
      status: err.response?.status,
      data: err.response?.data,
      message: err.message
    });

    // Clear loading toast
    toast.dismiss("create-student");

    // Handle specific error types
    if (err.response) {
      // Server responded with error
      const { status, data } = err.response;
      
      if (status === 400) {
        // Validation errors
        if (data.errors && Array.isArray(data.errors)) {
          data.errors.forEach(error => toast.error(error));
        } else if (data.message) {
          toast.error(data.message);
        } else {
          toast.error("Invalid data submitted. Please check all fields.");
        }
      } else if (status === 409) {
        // Conflict - duplicate email/enrollment
        toast.error(data.message || "User already exists with this email");
      } else if (status === 404) {
        // Not found - program/cohort doesn't exist
        toast.error("Selected program or cohort not found");
      } else if (status === 500) {
        toast.error("Server error. Please try again later.");
      } else {
        toast.error(data.message || "Failed to create student");
      }
    } else if (err.request) {
      // Request made but no response
      toast.error("Network error. Please check your connection.");
    } else {
      // Something else happened
      toast.error("An unexpected error occurred");
    }
  }
};
  const handleStudentChange = useCallback((e) => {
    setStudentForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }, []);

  // In AdminDashboard.js - Update handleCreateTeacher function
 const handleCreateTeacher = async (e) => {
  e.preventDefault();

  // Validate form data
  if (!teacherForm.fullName || !teacherForm.email || !teacherForm.password || !teacherForm.course) {
    toast.error("Please fill all required fields");
    return;
  }

  try {
    console.log("Creating teacher with:", teacherForm);

    const res = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/create-teacher`,
      teacherForm,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      },
    );

    console.log("Teacher created:", res.data);

    if (res.data.success) {
      toast.success("Teacher account created successfully!");
      
      // Check email status
      if (res.data.emailSent) {
        toast.success(`✓ Credentials have been emailed to ${teacherForm.email}`);
      } else {
        toast.warning(
          `⚠ Teacher created but email could not be sent. ` +
          `Please manually provide credentials to the teacher.`
        );
      }
      
      // Show credentials to admin
      if (res.data.credentials) {
        const { email, password, course } = res.data.credentials;
        
        alert(`
          TEACHER ACCOUNT CREATED
          ========================
          
          Teacher Details:
          • Name: ${teacherForm.fullName}
          • Email: ${email}
        
          • Password: ${password}
          
          ${res.data.emailSent ? 
            '✓ Credentials sent to teacher\'s email.' : 
            '⚠ Please provide credentials manually.'}
          
          IMPORTANT:
          • Teacher should change password on first login
          • Save these credentials securely
        `);
      }

      setShowTeacherModal(false);
      setTeacherForm({});
      fetchDashboardData();
    } else {
      toast.error(res.data.message || "Failed to create teacher");
    }
  } catch (err) {
    console.error("Create teacher error:", err);

    // Better error handling
    if (err.response) {
      // Server responded with error
      toast.error(
        err.response.data?.message ||
          err.response.data?.error ||
          "Failed to create teacher",
      );
    } else if (err.request) {
      // Request made but no response
      toast.error("No response from server. Please check your connection.");
    } else {
      // Something else
      toast.error("Error: " + err.message);
    }
  }
};

  const handleCreateCohort = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/cohorts`, cohortForm);
      toast.success("Cohort created successfully");
      setCohortForm({ programId: "", startMonth: "", startYear: "" });
      fetchDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create cohort");
    }
  };

  const handleEditTeacher = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/admin/update-teacher/${editingTeacher._id}`,
        teacherForm,
      );
      toast.success("Teacher updated successfully.");
      setShowEditTeacherModal(false);
      setEditingTeacher(null);
      setTeacherForm({});
      fetchDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update teacher");
    }
  };

  const handleEditStudent = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/admin/update-student/${editingStudent._id}`,
        studentForm,
      );

      console.log("Update response:", response.data);

      toast.success("Student updated successfully.");
      setShowEditStudentModal(false);
      setEditingStudent(null);
      setStudentForm({});
      fetchDashboardData();
    } catch (err) {
      console.error("Update error:", err.response?.data);
      toast.error(err.response?.data?.message || "Failed to update student");
    }
  };
  const handleSoftDeleteTeacher = async (teacherId) => {
    if (window.confirm("Are you sure you want to remove this teacher?")) {
      try {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/admin/soft-delete-teacher/${teacherId}`,
        );
        toast.success("Teacher removed successfully.");
        fetchDashboardData();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to remove teacher");
      }
    }
  };

  const handleSoftDeleteStudent = async (studentId) => {
    if (window.confirm("Are you sure you want to remove this student?")) {
      try {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/admin/soft-delete-student/${studentId}`,
        );
        toast.success("Student removed successfully.");
        fetchDashboardData();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to remove student");
      }
    }
  };

  // Add this with other modal components
const AdmissionDetailsModal = useMemo(() => {
  const ModalComponent = React.memo(({ 
    show, 
    onClose, 
    admission,
    programs,
    cohorts,
    setActiveTab
  }) => {
    const [processingStatus, setProcessingStatus] = useState("");
    const [selectedProgram, setSelectedProgram] = useState("");
    const [selectedCohort, setSelectedCohort] = useState("");
    const [availableCohorts, setAvailableCohorts] = useState([]);
    const [processingNotes, setProcessingNotes] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [showStudentForm, setShowStudentForm] = useState(false);
    const [studentFormData, setStudentFormData] = useState({
      fullName: "",
      fatherName: "",
      email: "",
      password: "",
      contact: "",
      altContact: "",
      dob: "",
      address: "",
      programId: "",
      cohortId: "",
      enrollmentId: "",
    });
    // Add this state
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [createdStudentInfo, setCreatedStudentInfo] = useState(null);
    
    // Initialize form data when admission is loaded
    useEffect(() => {
      if (admission) {
        setStudentFormData({
          fullName: admission.fullName || "",
          fatherName: admission.fatherName || "",
          email: admission.email || "",
          password: generateTemporaryPassword(), // Auto-generate password
          contact: admission.contact || "",
          altContact: admission.altContact || "",
          dob: admission.dob || "",
          address: admission.address || "",
          programId: "",
          cohortId: "",
          enrollmentId: admission.enrollmentId || "",
        });
      }
    }, [admission]);
    
    // Generate temporary password function
    const generateTemporaryPassword = () => {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let password = "";
      for (let i = 0; i < 8; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return password;
    };
    
    // Filter cohorts based on selected program
    useEffect(() => {
      if (selectedProgram) {
        const filteredCohorts = cohorts.filter(
          (c) => c.programId?._id === selectedProgram || c.programId === selectedProgram
        );
        setAvailableCohorts(filteredCohorts);
      } else {
        setAvailableCohorts([]);
      }
    }, [selectedProgram, cohorts]);
    
    const handleProcessApplication = () => {
      if (!processingStatus) {
        toast.error("Please select a status");
        return;
      }
      
      if (processingStatus === "rejected") {
        // Reject the application directly
        handleRejectApplication();
        return;
      }
      
      if (processingStatus === "approved") {
        if (!selectedProgram || !selectedCohort) {
          toast.error("Please select program and cohort for approved students");
          return;
        }
        
        // Update student form with selected program and cohort
        setStudentFormData(prev => ({
          ...prev,
          programId: selectedProgram,
          cohortId: selectedCohort
        }));
        
        // Show student form for final details
        setShowStudentForm(true);
      }
    };
    
    const handleRejectApplication = async () => {
      setIsProcessing(true);
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/admission/process-application/${admission._id}`,
          {
            status: "rejected",
            notes: processingNotes
          }
        );
        
        if (response.data.success) {
          toast.success("Application rejected successfully");
          onClose();
          fetchAdmissionDetails();
        }
      } catch (error) {
        console.error("Error rejecting application:", error);
        toast.error(error.response?.data?.message || "Failed to reject application");
      } finally {
        setIsProcessing(false);
      }
    };
    
    const handleCreateStudent = async (e) => {
      e.preventDefault();
      
      if (!studentFormData.programId) {
        toast.error("Please select a program");
        return;
      }
      
      if (!studentFormData.cohortId) {
        toast.error("Please select a cohort");
        return;
      }
      
      if (!studentFormData.password) {
        toast.error("Password is required");
        return;
      }
      
      setIsProcessing(true);
      try {
        // First, create the student account
        const studentData = {
          ...studentFormData,
          role: "student",
          status: "active",
          admissionId: admission._id
        };
        
        console.log("Creating student with data:", studentData);
        
        const createStudentResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/admin/create-student`,
          studentData
        );
        
        if (createStudentResponse.data.success) {
          // ============================================
          // CHECK EMAIL STATUS AND SHOW APPROPRIATE MESSAGE
          // ============================================
          let emailStatusMessage = "";
          if (createStudentResponse.data.emailSent) {
            emailStatusMessage = `✓ Credentials have been emailed to ${studentData.email}`;
          } else {
            emailStatusMessage = `⚠ Student created but email could not be sent to ${studentData.email}`;
          }
          
          // Get program and cohort names for display
          const programName = programs.find(p => p._id === studentData.programId)?.name || "N/A";
          const cohortCode = cohorts.find(c => c._id === studentData.cohortId)?.cohortCode || "N/A";
          const { enrollmentId, email, password } = createStudentResponse.data.credentials || {};
          
          // Show comprehensive success message
          alert(`
            ✅ STUDENT ACCOUNT CREATED SUCCESSFULLY!
            
            ${emailStatusMessage}
            
            Student Details:
            • Name: ${studentData.fullName}
            • Email: ${email || studentData.email}
            • Enrollment ID: ${enrollmentId || studentData.enrollmentId}
            • Program: ${programName}
            • Cohort: ${cohortCode}
            • Password: ${password || studentData.password}
            
            ${createStudentResponse.data.emailSent ? 
              'The student will receive these credentials via email.' : 
              'Please manually provide these credentials to the student.'}
            
            The student can now login with these credentials.
          `);
          
          // Show toast notification
          if (createStudentResponse.data.emailSent) {
            toast.success("Student account created and credentials emailed!");
          } else {
            toast.success("Student account created successfully!");
            toast.warning("Email could not be sent. Please provide credentials manually.", { autoClose: 5000 });
          }
          
          // Then, update admission status to approved
          const updateAdmissionResponse = await axios.put(
            `${process.env.REACT_APP_BACKEND_URL}/admission/process-application/${admission._id}`,
            {
              status: "approved",
              programId: studentFormData.programId,
              cohortId: studentFormData.cohortId,
              notes: processingNotes,
              studentId: createStudentResponse.data.student?._id || createStudentResponse.data.studentId
            }
          );
          
          if (updateAdmissionResponse.data.success) {
            toast.success("Application approved!");
            
            // Close modal
            onClose();
            
            // Refresh admission list
            fetchAdmissionDetails();
            
            // Redirect to students tab
            setTimeout(() => {
              setActiveTab("students");
             
              
              // Refresh students list
              const refreshStudents = async () => {
                try {
                  const allStuRes = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/admin/students`
                  );
                  setAllStudents(allStuRes.data);
                } catch (err) {
                  console.error("Error refreshing students:", err);
                }
              };
              refreshStudents();
            }, 1000);
          }
        }
      } catch (error) {
        console.error("Error creating student:", error.response?.data || error);
        toast.error(error.response?.data?.message || "Failed to create student account");
      } finally {
        setIsProcessing(false);
      }
    };
    
    const handleStudentFormChange = (e) => {
      const { name, value } = e.target;
      setStudentFormData(prev => ({
        ...prev,
        [name]: value
      }));
    };
    
    const handleDownload = (filename) => {
      if (filename) {
        window.open(`${process.env.REACT_APP_IMG_URL}uploads/${filename}`, '_blank');
      } else {
        toast.error("File not found");
      }
    };
    
    if (!show || !admission) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal-box wide-modal">
          <div className="modal-header">
            <h2>Admission Details - {admission.enrollmentId}</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          
          <div className="admission-details-modal">
            
            {!showStudentForm ? (
              <>
                {/* Application Status Processing Section */}
                <div className="process-application-section">
                  <h4>Process Application</h4>
                  <div className="process-form">
                    <div className="form-group">
                      <label>Status </label>
                      <select 
                        value={processingStatus} 
                        onChange={(e) => setProcessingStatus(e.target.value)}
                        className="status-select"
                      >
                        <option value="">Select Action</option>
                        <option value="approved">Approve & Create Student Account</option>
                        <option value="rejected">Reject Application</option>
                      </select>
                    </div>
                    
                    {processingStatus === "approved" && (
                      <>
                        <div className="form-group">
                          <label>Program </label>
                          <select 
                            value={selectedProgram} 
                            onChange={(e) => setSelectedProgram(e.target.value)}
                            className="program-select"
                          >
                            <option value="">Select Program</option>
                            {programs.map((program) => (
                              <option key={program._id} value={program._id}>
                                {program.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="form-group">
                          <label>Cohort/Batch *</label>
                          <select 
                            value={selectedCohort} 
                            onChange={(e) => setSelectedCohort(e.target.value)}
                            className="cohort-select"
                            disabled={!selectedProgram}
                          >
                            <option value="">Select Cohort</option>
                            {availableCohorts.map((cohort) => (
                              <option key={cohort._id} value={cohort._id}>
                                {cohort.cohortCode}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                    
                    <div className="form-group">
                      <label>Notes/Comments</label>
                      <textarea 
                        value={processingNotes}
                        onChange={(e) => setProcessingNotes(e.target.value)}
                        placeholder="Add any notes about this application..."
                        rows="3"
                      />
                    </div>
                    
                    <div className="process-actions">
                      <button 
                        className="btn-view"
                        onClick={handleProcessApplication}
                        disabled={isProcessing || !processingStatus}
                      >
                        {isProcessing ? "Processing..." : "Process Application"}
                      </button>
                      
                      {processingStatus === "approved" && (
                        <div className="approval-note">
                          <small>
                            <FaBell /> You'll be able to review and create student account in next step
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Admission Details Viewing Section */}
                <div className="detail-section">
                  <h4>Personal Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <strong>Enrollment ID:</strong> {admission.enrollmentId}
                    </div>
                    <div className="detail-item">
                      <strong>Full Name:</strong> {admission.fullName}
                    </div>
                    <div className="detail-item">
                      <strong>Father's Name:</strong> {admission.fatherName}
                    </div>
                    <div className="detail-item">
                      <strong>Date of Birth:</strong> {admission.dob || 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Contact Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <strong>Email:</strong> {admission.email}
                    </div>
                    <div className="detail-item">
                      <strong>Contact:</strong> {admission.contact}
                    </div>
                    <div className="detail-item">
                      <strong>Alternate Contact:</strong> {admission.altContact || 'N/A'}
                    </div>
                    <div className="detail-item">
                      <strong>Address:</strong> {admission.address || 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Academic Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <strong>Course:</strong> {admission.course || admission.courseName || 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Documents</h4>
                  <div className="documents-grid">
                    {admission.aadhar && (
                      <button 
                        className="doc-btn"
                        onClick={() => handleDownload(admission.aadhar)}
                      >
                        <FaFileAlt /> Aadhar Card
                      </button>
                    )}
                    {admission.tenth && (
                      <button 
                        className="doc-btn"
                        onClick={() => handleDownload(admission.tenth)}
                      >
                        <FaFileAlt /> 10th Marksheet
                      </button>
                    )}
                    {admission.twelfth && (
                      <button 
                        className="doc-btn"
                        onClick={() => handleDownload(admission.twelfth)}
                      >
                        <FaFileAlt /> 12th Marksheet
                      </button>
                    )}
                    {admission.grad && (
                      <button 
                        className="doc-btn"
                        onClick={() => handleDownload(admission.grad)}
                      >
                        <FaFileAlt /> Graduation
                      </button>
                    )}
                    {admission.pgCert && (
                      <button 
                        className="doc-btn"
                        onClick={() => handleDownload(admission.pgCert)}
                      >
                        <FaFileAlt /> PG Certificate
                      </button>
                    )}
                    {admission.photo && (
                      <button 
                        className="doc-btn"
                        onClick={() => handleDownload(admission.photo)}
                      >
                        <FaFileAlt /> Photo
                      </button>
                    )}
                    {admission.paymentScreenshot && (
                      <button 
                        className="doc-btn"
                        onClick={() => handleDownload(admission.paymentScreenshot)}
                      >
                        <FaFileAlt /> Payment Receipt
                      </button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              /* Student Creation Form */
              <div className="student-creation-form">
                <div className="form-header">
                  <h3>Create Student Account</h3>
                  <p className="form-subtitle">
                    Review and complete student details for {admission.fullName}
                  </p>
                  <button 
                    className="btn-back"
                    onClick={() => setShowStudentForm(false)}
                  >
                    ← Back to Application Details
                  </button>
                </div>
                
                <form onSubmit={handleCreateStudent} className="modal-form">
                  <div className="form-section">
                    <h4>Basic Information</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Full Name *</label>
                        <input
                          type="text"
                          name="fullName"
                          value={studentFormData.fullName}
                          onChange={handleStudentFormChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Father's Name *</label>
                        <input
                          type="text"
                          name="fatherName"
                          value={studentFormData.fatherName}
                          onChange={handleStudentFormChange}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={studentFormData.email}
                          onChange={handleStudentFormChange}
                          required
                          disabled
                        />
                        <small className="field-note">Email cannot be changed</small>
                      </div>
                      <div className="form-group">
                        <label>Password *</label>
                        <div className="password-field">
                          <input
                            type="text"
                            name="password"
                            value={studentFormData.password}
                            onChange={handleStudentFormChange}
                            required
                          />
                          <button 
                            type="button" 
                            className="btn-generate"
                            onClick={() => setStudentFormData(prev => ({
                              ...prev,
                              password: generateTemporaryPassword()
                            }))}
                          >
                            Regenerate
                          </button>
                        </div>
                        <small className="field-note">Auto-generated password</small>
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-section">
                    <h4>Contact Details</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Contact Number *</label>
                        <input
                          type="text"
                          name="contact"
                          value={studentFormData.contact}
                          onChange={handleStudentFormChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Alternate Contact</label>
                        <input
                          type="text"
                          name="altContact"
                          value={studentFormData.altContact}
                          onChange={handleStudentFormChange}
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Date of Birth</label>
                        <input
                          type="date"
                          name="dob"
                          value={studentFormData.dob}
                          onChange={handleStudentFormChange}
                        />
                      </div>
                      <div className="form-group">
                        <label>Enrollment ID</label>
                        <input
                          type="text"
                          name="enrollmentId"
                          value={studentFormData.enrollmentId}
                          onChange={handleStudentFormChange}
                          disabled
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label>Address</label>
                      <textarea
                        name="address"
                        value={studentFormData.address}
                        onChange={handleStudentFormChange}
                        rows="2"
                      />
                    </div>
                  </div>
                  
                  <div className="form-section">
                    <h4>Academic Assignment</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Program *</label>
                        <select
                          name="programId"
                          value={studentFormData.programId}
                          onChange={handleStudentFormChange}
                          required
                        >
                          <option value="">Select Program</option>
                          {programs.map((program) => (
                            <option key={program._id} value={program._id}>
                              {program.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Cohort/Batch *</label>
                        <select
                          name="cohortId"
                          value={studentFormData.cohortId}
                          onChange={handleStudentFormChange}
                          required
                          disabled={!studentFormData.programId}
                        >
                          <option value="">Select Cohort</option>
                          {cohorts
                            .filter(c => 
                              c.programId?._id === studentFormData.programId || 
                              c.programId === studentFormData.programId
                            )
                            .map((cohort) => (
                              <option key={cohort._id} value={cohort._id}>
                                {cohort.cohortCode}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setShowStudentForm(false)}
                      disabled={isProcessing}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isProcessing}
                    >
                      {isProcessing ? "Creating..." : "Create Student Account"}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  });

  ModalComponent.displayName = "AdmissionDetailsModal";
  return ModalComponent;
}, []);

  const handleApproveStudent = async (studentId) => {
    try {
      if (!studentForm.cohortId) {
        toast.error("Please select a cohort before approving");
        return;
      }

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/admin/approve-student/${studentId}`,
        {
          cohortId: studentForm.cohortId,
        },
      );

      toast.success("Student approved and assigned to cohort");
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to approve student");
    }
  };

  const handleRejectStudent = async (studentId) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/admin/reject-student/${studentId}`,
      );
      toast.success("Student rejected");
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to reject student");
    }
  };

  const handleViewStudent = (student) => {
    setEditingStudent(student);
    setStudentForm({
      fullName: student.fullName || "",
      email: student.email || "",
      contact: student.contact || "",
      enrollmentId: student.enrollmentId || "",
      course: student.course || "",
      status: student.status || "pending",
      programId: student.programId?._id || student.programId || "",
      cohortId: student.cohortId?._id || student.cohortId || "",
    });
    setShowEditStudentModal(true);
  };

  // NEW: Classroom Management Handlers

  const handleAssignTeacher = async (e) => {
    e.preventDefault();

    if (!assignForm.programId) {
      toast.error("Please select a program");
      return;
    }

    if (!assignForm.cohortId) {
      toast.error("Please select a cohort");
      return;
    }

    if (!assignForm.subjectId) {
      toast.error("Please select a subject");
      return;
    }

    if (!assignForm.teacherId) {
      toast.error("Please select a teacher");
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/teacher/subject-teacher`, {
        cohortId: assignForm.cohortId,
        subjectId: assignForm.subjectId,
        teacherId: assignForm.teacherId,
      });

      toast.success("Teacher assigned successfully");

      // reset form
      setAssignForm({
        programId: "",
        cohortId: "",
        subjectId: "",
        teacherId: "",
      });
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("This subject is already assigned to the selected cohort");
      } else {
        toast.error(err.response?.data?.message || "Failed to assign teacher");
      }
    }
  };

  // Add this with other handler functions
  const fetchBatchDetails = async () => {
    try {
      setLoadingBatchDetails(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/batch-details`,
      );
      setBatchDetails(response.data);
    } catch (error) {
      toast.error("Failed to fetch batch details");
      console.error("Error fetching batch details:", error);
    } finally {
      setLoadingBatchDetails(false);
    }
  };

  // Add this useEffect
  useEffect(() => {
    if (activeTab === "batchDetails") {
      fetchBatchDetails();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!assignForm.programId) return;

    const loadProgramData = async () => {
      try {
        const config = {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        };

        const [subjectRes, cohortRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/subjects/${assignForm.programId}`,
            config,
          ),
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/cohorts/active/${assignForm.programId}`,
            config,
          ),
        ]);

        setSubjects(subjectRes.data || []);
        setActiveCohorts(cohortRes.data || []);
      } catch (err) {
        console.error("Failed to load program data", err);
        toast.error("Failed to load subjects");
        setSubjects([]);
        setActiveCohorts(null);
      }
    };

    loadProgramData();
  }, [assignForm.programId]);

  const renderLeaveApplications = () => (
  <div className="leave-applications-section">
    <div className="section-header">
      <h3>Student Leave Applications</h3>
      <div className="stats-summary">
        <span className="stat-badge">
          Total Applications: <strong>{leaveApplications.length}</strong>
        </span>
      </div>
    </div>

    {loadingLeaves ? (
      <div className="loading-container">
        <p>Loading leave applications...</p>
      </div>
    ) : leaveApplications.length === 0 ? (
      <div className="no-data">
        <p>No leave applications found</p>
      </div>
    ) : (
      <>
        <table className="approval-table">
          <thead>
            <tr>
              <th>Student Name</th>
              <th>Enrollment ID</th>
              <th>Leave Type</th>
              <th>Period</th>
              <th>Duration</th>
              <th>Reason</th>
              <th>Applied On</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {leaveApplications.map((leave) => (
              <tr key={leave._id}>
                <td>
                  <span className="hide-for-desktop">Student Name: </span>{leave.studentName}
                </td>
                <td><span className="hide-for-desktop">Enrollement ID: </span>{leave.enrollmentId}</td>
                <td><span className="hide-for-desktop">Leave Type: </span>
                  <span className="leave-type-badge">
                    {leave.leaveType === 'medical' ? '🏥 Medical' :
                     leave.leaveType === 'personal' ? '👤 Personal' :
                     leave.leaveType === 'emergency' ? '🚨 Emergency' :
                     leave.leaveType === 'academic' ? '📚 Academic' : '📝 Other'}
                  </span>
                </td>
                <td><span className="hide-for-desktop">Start Date: </span>
                  {new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()}
                </td>
                <td><span className="hide-for-desktop">Total Days: </span>{leave.totalDays} day(s)</td>
                <td className="reason-cell"><span className="hide-for-desktop">Reason: </span>
                  {leave.reason.length > 50 ? leave.reason.substring(0, 50) + '...' : leave.reason}
                </td>
                <td><span className="hide-for-desktop">Created At: </span>{new Date(leave.createdAt).toLocaleDateString()}</td>
                <td><span className="hide-for-desktop">Status: </span>
                  <span className={`status-badge ${leave.status}`}>
                    {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                  </span>
                </td>
                <td className="action-buttons">
                  <button
                    className="btn-view"
                    onClick={() => {
                      setSelectedLeave(leave);
                      setShowLeaveModal(true);
                    }}
                  >
                    <FaEye /> View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        <div className="table-footer">
          <p>Showing {leaveApplications.length} leave applications</p>
        </div>
      </>
    )}
  </div>
);

  const renderAdmissionDetails = () => (
  <div className="admission-details-section">
    <div className="section-header">
      <h3>Admission Form Submissions</h3>
      <div className="stats-summary">
        <span className="stat-badge">
          Total Submissions: <strong>{admissionDetails.length}</strong>
        </span>
      </div>
    </div>

    {loadingAdmissions ? (
      <div className="loading-container">
        <p>Loading admission details...</p>
      </div>
    ) : admissionDetails.length === 0 ? (
      <div className="no-data">
        <p>No admission submissions found</p>
      </div>
    ) : (
      <>
        <table className="approval-table">
          <thead>
            <tr>
              <th>Enrollment ID</th>
              <th>Full Name</th>
              <th>Father Name</th>
              <th>Email</th>
              <th>Contact</th>
              <th>Course</th>
              <th>Applied Date</th>
             
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admissionDetails.map((admission) => (
              <tr key={admission._id}>
                <td><span className="hide-for-desktop">Enrollment ID: </span>
                  {admission.enrollmentId}
                </td>
                <td><span className="hide-for-desktop">Full Name: </span>{admission.fullName}</td>
                <td><span className="hide-for-desktop">Father Name: </span>{admission.fatherName}</td>
                <td><span className="hide-for-desktop">Email: </span>{admission.email}</td>
                <td><span className="hide-for-desktop">Contact: </span>{admission.contact}</td>
                <td><span className="hide-for-desktop">Course Name: </span>{admission.course || admission.courseName || "Not specified"}</td>
                <td><span className="hide-for-desktop">Applied Date: </span>{new Date(admission.createdAt).toLocaleDateString()}</td>
               
                <td className="action-buttons">
                  <button
                    className="btn-view"
                    onClick={() => {
                      setSelectedAdmission(admission);
                      setShowAdmissionModal(true);
                    }}
                  >
                    <FaEye /> View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        <div className="table-footer">
          <p>Showing {admissionDetails.length} admission records</p>
        </div>
      </>
    )}
  </div>
);

  const renderGoogleClassroom = () => (
   <GoogleClassroom />
  );

  const renderTeachers = () => (
    <div className="teachers-section">
      <div className="teachers-header">
        <h3>All Teachers</h3>
        <button
          className="btn-primary add-teacher-btn"
          onClick={() => setShowTeacherModal(true)}
        >
          <FaPlus /> Add Teacher
        </button>
      </div>

      <table className="approval-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Contact</th>
            <th>Course</th>
            <th>Subject</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {teachers.map((t) => (
            <tr key={t._id}>
              <td><span className="hide-for-desktop">Full Name: </span>{t.fullName}</td>
              <td><span className="hide-for-desktop">Email: </span>{t.email}</td>
              <td><span className="hide-for-desktop">Contact: </span>{t.contact}</td>
              <td><span className="hide-for-desktop">Course: </span>{t.course || "—"}</td>
              <td><span className="hide-for-desktop">Subject: </span>{t.subject || "—"}</td>
              <td><span className="hide-for-desktop">Status: </span>
                <span className={`status-badge ${t.status}`}>{t.status}</span>
              </td>
              <td className="action-buttons">
                <button
                  className="btn-edit"
                  onClick={() => {
                    setEditingTeacher(t);
                    setTeacherForm({
                      fullName: t.fullName,
                      email: t.email,
                      contact: t.contact,
                      course: t.course,
                      subject: t.subject,
                      qualifications: t.qualifications,
                      experience: t.experience,
                    });
                    setShowEditTeacherModal(true);
                  }}
                >
                  <FaEdit /> Edit
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleSoftDeleteTeacher(t._id)}
                >
                  <FaTrash /> Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const getCohortDisplayStatus = (cohort) => {
    const durationMonths = cohort.programId?.name === "PTT" ? 12 : 6;

    const startDate = new Date(cohort.startYear, cohort.startMonth - 1, 1);

    const endDate = new Date(
      cohort.startYear,
      cohort.startMonth - 1 + durationMonths,
      0,
    );

    const today = new Date();

    if (today < startDate) return "Yet to Start";
    if (today > endDate) return "Course Ended";
    return "Active";
  };

  const renderCohorts = () => (
    <div className="cohorts-section">
      <h3>Create Batch</h3>

      <form onSubmit={handleCreateCohort} className="class-form">
        <label>Course / Program</label>
        <select
          value={cohortForm.programId}
          onChange={(e) =>
            setCohortForm({ ...cohortForm, programId: e.target.value })
          }
          required
        >
          <option value="">Select Program</option>
          {programs.map((p) => (
            <option key={p._id} value={p._id}>
              {p.name}
            </option>
          ))}
        </select>

        <label>Start Month</label>
        <select
          value={cohortForm.startMonth}
          onChange={(e) =>
            setCohortForm({ ...cohortForm, startMonth: Number(e.target.value) })
          }
          required
        >
          <option value="">Select Month</option>
          <option value={1}>January</option>
          <option value={2}>February</option>
          <option value={3}>March</option>
          <option value={4}>April</option>
          <option value={5}>May</option>
          <option value={6}>June</option>
          <option value={7}>July</option>
          <option value={8}>August</option>
          <option value={9}>September</option>
          <option value={10}>October</option>
          <option value={11}>November</option>
          <option value={12}>December</option>
        </select>

        <label>Start Year</label>
        <input
          type="number"
          value={cohortForm.startYear}
          onChange={(e) =>
            setCohortForm({ ...cohortForm, startYear: e.target.value })
          }
          required
        />

        <button className="btn-primary">Create Batch</button>
      </form>

      <h3 style={{ marginTop: "30px" }}>Existing Batches</h3>

      <table className="approval-table">
        <thead>
          <tr>
            <th>Cohort Code</th>
            <th>Course</th>
            <th>Status</th>
            <th>Start</th>
            <th>Expected End</th>
          </tr>
        </thead>
        <tbody>
          {cohorts.map((c) => (
            <tr key={c._id}>
              <td><span className="hide-for-desktop">Cohort Code: </span>{c.cohortCode}</td>
              <td><span className="hide-for-desktop">Course: </span>{c.programId?.name}</td>
              <td><span className="hide-for-desktop">Status: </span>
                <span
                  className={`status-badge ${getCohortDisplayStatus(c)
                    .toLowerCase()
                    .replace(/\s/g, "-")}`}
                >
                  {getCohortDisplayStatus(c)}
                </span>
              </td>
              <td><span className="hide-for-desktop">Start Month/Year: </span>
                {c.startMonth}/{c.startYear}
              </td>
              <td><span className="hide-for-desktop">Duration: </span>
                {(() => {
                  const durationMonths = c.programId?.name === "PTT" ? 12 : 6;

                  const endDate = new Date(
                    c.startYear,
                    c.startMonth - 1 + durationMonths,
                  );

                  return endDate.toLocaleDateString("en-GB");
                })()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderStudents = () => (
    <div className="students-section">
      <div className="section-header">
        <h3>All Students</h3>
        <button
          className="btn-primary"
          onClick={() => setShowAddStudentModal(true)}
        >
          <FaPlus /> Add Student
        </button>
      </div>

      {allStudents.length === 0 ? (
        <p>No students found</p>
      ) : (
        <table className="approval-table">
          <thead>
            <tr>
              <th>Enrollment ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Contact</th>
              <th>Status</th>
              <th>Course</th>
              <th>Batch</th>
              <th>Admission Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {allStudents.map((stu) => (
              <tr key={stu._id}>
                <td>
                  <span className="hide-for-desktop">Enrollment ID: </span>{stu.enrollmentId}
                </td>
                <td><span className="hide-for-desktop">Full Name: </span>{stu.fullName}</td>
                <td><span className="hide-for-desktop">Email: </span>{stu.email}</td>
                <td><span className="hide-for-desktop">Contact Number: </span>{stu.contact}</td>
                <td style={{ textTransform: "capitalize" }}><span className="hide-for-desktop">Status: </span>
                  <span className={`status-badge ${stu.status}`}>
                    {stu.status}
                  </span>
                </td>
                <td><span className="hide-for-desktop">Course: </span>{stu.course}</td>
                <td><span className="hide-for-desktop">Batch: </span>{stu.batch}</td>
                <td><span className="hide-for-desktop">Admission Date: </span>{new Date(stu.createdAt).toLocaleDateString()}</td>
                <td className="action-buttons">
                  <button
                    className="btn-view"
                    onClick={() => handleViewStudent(stu)}
                  >
                    <FaEye /> View/Edit
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => handleSoftDeleteStudent(stu._id)}
                  >
                    <FaTrash /> Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const statsChartData = [
    {
      name: "Students",
      value: stats.totalStudents,
      fill: "#116dff",
    },
    {
      name: "Teachers",
      value: stats.totalTeachers,
      fill: "#ee5951",
    },
    {
      name: "Cohorts",
      value: stats.totalCohorts,
      fill: "#116dff",
    },
    {
      name: "Active Classes",
      value: stats.activeClasses,
      fill: "#ee5951",
    },
  ];

  const renderDashboard = () => (
    <div className="admin-dashboard">
      <div className="stats-cards">
        <div className="stat-card">
          <FaUsers className="stat-icon" />
          <div className="stat-info">
            <h3>{stats.totalStudents}</h3>
            <p>Students</p>
          </div>
        </div>

        <div className="stat-card">
          <FaChalkboardTeacher className="stat-icon" />
          <div className="stat-info">
            <h3>{stats.totalTeachers}</h3>
            <p>Teachers</p>
          </div>
        </div>

        <div className="stat-card">
          <FaClipboardList className="stat-icon" />
          <div className="stat-info">
            <h3>{stats.totalCohorts}</h3>
            <p>Batches</p>
          </div>
        </div>

        <div className="stat-card">
          <FaCalendarAlt className="stat-icon" />
          <div className="stat-info">
            <h3>{stats.activeClasses}</h3>
            <p>Active Classes</p>
          </div>
        </div>
      </div>

      <div className="dashboard-chart">
        <h3 className="blue-color" style={{ marginBottom: "20px" }}>
          Overall Statistics
        </h3>

        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={statsChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="value">
              {statsChartData.map((entry, index) => (
                <cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* <div className="pending-approvals">
        <h3>Pending Student Approvals</h3>
        {pendingStudents.length === 0 ? (
          <p>No pending approvals</p>
        ) : (
          <table className="approval-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Contact</th>
                <th>Course</th>
                <th>Applied Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingStudents.map((student) => (
                <tr key={student._id}>
                  <td>{student.fullName}</td>
                  <td>{student.email}</td>
                  <td>{student.contact}</td>
                  <td>{student.course || "Not specified"}</td>
                  <td>{new Date(student.createdAt).toLocaleDateString()}</td>
                  <td className="action-buttons">
                    <button
                      className="btn-approve"
                      onClick={() => handleApproveStudent(student._id)}
                    >
                      <FaCheckCircle /> Approve
                    </button>
                    <button
                      className="btn-reject"
                      onClick={() => handleRejectStudent(student._id)}
                    >
                      <FaTimesCircle /> Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div> */}
    </div>
  );

  // Add this function with other render functions (after renderSubjectTeacherAssignment)
  const renderBatchDetails = () => {
    console.log("DEBUG - batchDetails:", batchDetails);
    console.log("DEBUG - programs:", programs);

    // Filter batch details based on selected filters
    const filteredBatches = batchDetails.filter((batch) => {
      console.log("Batch filter check:", {
        batchName: batch.cohortCode,
        batchProgramName: batch.programName,
        filterProgramId: batchFilters.programId,
        filterCohortId: batchFilters.cohortId,
      });

      // Handle program filter - match by program name instead of ID
      if (batchFilters.programId) {
        // Find the selected program by ID
        const selectedProgram = programs.find(
          (p) => p._id === batchFilters.programId,
        );
        console.log("Selected program:", selectedProgram);

        // Compare program names instead of IDs
        if (selectedProgram && batch.programName !== selectedProgram.name) {
          console.log(
            "Program filter FAILED - names don't match:",
            batch.programName,
            "vs",
            selectedProgram.name,
          );
          return false;
        }
      }

      // Handle cohort filter
      if (batchFilters.cohortId && batch._id !== batchFilters.cohortId) {
        console.log("Cohort filter FAILED");
        return false;
      }

      console.log("Filter PASSED");
      return true;
    });

    console.log("Filtered batches:", filteredBatches.length);

    // Get unique programs from batch details - match by name
    const uniqueProgramNames = [
      ...new Set(
        batchDetails.map((batch) => batch.programName).filter((name) => name),
      ),
    ];
    console.log("Unique program names:", uniqueProgramNames);

    const programOptions = programs.filter((p) =>
      uniqueProgramNames.includes(p.name),
    );
    console.log("Program options:", programOptions);

    // Get cohorts for selected program
    const cohortOptions = cohorts.filter((c) => {
      if (!batchFilters.programId) return false;

      const selectedProgram = programs.find(
        (p) => p._id === batchFilters.programId,
      );
      if (!selectedProgram) return false;

      // Match cohort's program name with selected program name
      return c.programId?.name === selectedProgram.name;
    });

    console.log("Cohort options:", cohortOptions);

    return (
      <div className="batch-details-section">
        <div className="section-header">
          <h3>Batch Details - Active Cohorts</h3>

          {/* Filters */}
          <div className="batch-filters">
            <div className="filter-group">
              <label>Filter by Course:</label>
              <select
                value={batchFilters.programId}
                onChange={(e) => {
                  console.log("Selected program ID:", e.target.value);
                  const selectedProgram = programs.find(
                    (p) => p._id === e.target.value,
                  );
                  console.log("Selected program:", selectedProgram);
                  setBatchFilters({
                    programId: e.target.value,
                    cohortId: "", // Reset cohort when program changes
                  });
                }}
                className="filter-select"
              >
                <option value="">All Courses</option>
                {programOptions.map((program) => (
                  <option key={program._id} value={program._id}>
                    {program.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Filter by Batch:</label>
              <select
                value={batchFilters.cohortId}
                onChange={(e) => {
                  console.log("Selected cohort:", e.target.value);
                  setBatchFilters({
                    ...batchFilters,
                    cohortId: e.target.value,
                  });
                }}
                className="filter-select"
                disabled={!batchFilters.programId || cohortOptions.length === 0}
              >
                <option value="">All Batches</option>
                {cohortOptions.map((cohort) => (
                  <option key={cohort._id} value={cohort._id}>
                    {cohort.cohortCode}
                  </option>
                ))}
              </select>
            </div>

            <button
              className="btn-secondary"
              onClick={() => {
                console.log("Clearing filters");
                setBatchFilters({ programId: "", cohortId: "" });
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="filter-summary">
          <p>
            Showing <strong>{filteredBatches.length}</strong> of{" "}
            <strong>{batchDetails.length}</strong> batches
            {batchFilters.programId && (
              <>
                {" "}
                for{" "}
                <strong>
                  {programs.find((p) => p._id === batchFilters.programId)?.name}
                </strong>
              </>
            )}
            {batchFilters.cohortId && (
              <>
                {" "}
                in batch{" "}
                <strong>
                  {
                    cohorts.find((c) => c._id === batchFilters.cohortId)
                      ?.cohortCode
                  }
                </strong>
              </>
            )}
          </p>
        </div>

        {loadingBatchDetails ? (
          <p>Loading batch details...</p>
        ) : filteredBatches.length === 0 ? (
          <div className="no-results">
            <p>No batches found matching the selected filters</p>
            {batchFilters.programId && (
              <p style={{ marginTop: "10px" }}>
                Selected:{" "}
                {programs.find((p) => p._id === batchFilters.programId)?.name}
              </p>
            )}
            <button
              className="btn-primary"
              onClick={() => setBatchFilters({ programId: "", cohortId: "" })}
            >
              Show All Batches
            </button>
          </div>
        ) : (
          filteredBatches.map((batch) => (
            <div key={batch._id} className="batch-card">
              <div className="batch-header">
                <h4>
                  {batch.cohortCode} - {batch.programName}
                  <span
                    className={`status-badge ${(batch.status || "active").toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {batch.status || "Active"}
                  </span>
                </h4>
                <div className="batch-dates">
                  <span>Start: {batch.startDate || "N/A"}</span>
                  <span>End: {batch.endDate || "N/A"}</span>
                  <span>Duration: {batch.duration || "N/A"}</span>
                </div>
              </div>

              <div className="batch-content">
                {/* Subjects and Teachers Section */}
                <div className="subjects-section">
                  <h5>Subjects & Teachers</h5>
                  {!batch.subjects || batch.subjects.length === 0 ? (
                    <p>No subjects assigned</p>
                  ) : (
                    <table className="inner-table">
                      <thead>
                        <tr>
                          <th>Subject</th>
                          <th>Semester</th>
                          <th>Teacher</th>
                          <th>Contact</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batch.subjects.map((subject, index) => (
                          <tr key={subject._id || index}>
                            <td><span className="hide-for-desktop">Subject Name: </span>{subject.name || "N/A"}</td>
                            <td><span className="hide-for-desktop">Semester: </span>{subject.semester || "N/A"}</td>
                            <td><span className="hide-for-desktop">Teacher: </span>{subject.teacherName || "Not assigned"}</td>
                            <td><span className="hide-for-desktop">Contact: </span>{subject.teacherContact || "N/A"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Students Section */}
                <div className="students-section">
                  <h5>
                    Students ({batch.students ? batch.students.length : 0})
                  </h5>
                  {!batch.students || batch.students.length === 0 ? (
                    <p>No students enrolled</p>
                  ) : (
                    <table className="inner-table">
                      <thead>
                        <tr>
                          <th>Enrollment ID</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Contact</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batch.students.map((student, index) => (
                          <tr key={student._id || index}>
                            <td><span className="hide-for-desktop">Enrollment ID: </span>{student.enrollmentId || "N/A"}</td>
                            <td><span className="hide-for-desktop">Name: </span>{student.fullName || "N/A"}</td>
                            <td><span className="hide-for-desktop">Email: </span>{student.email || "N/A"}</td>
                            <td><span className="hide-for-desktop">Contact: </span>{student.contact || "N/A"}</td>
                            <td><span className="hide-for-desktop">Status: </span>
                              <span
                                className={`status-badge ${student.status || "active"}`}
                              >
                                {student.status || "Active"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  const visibleAssignments = assignForm.cohortId
    ? assignedSubjects.filter((a) => a.cohortId?._id === assignForm.cohortId)
    : assignedSubjects;

 const renderSubjectTeacherAssignment = () => {
  // Check if data is loaded
  if (!programs || !activeCohorts || !subjects || !teachers) {
    return (
      <div className="schedule-class">
        <h3>Assign Teacher to Subject</h3>
        <div className="loading-message">Loading data...</div>
      </div>
    );
  }

  return (
    <div className="schedule-class">
      <h3>Assign Teacher to Subject</h3>

      <form onSubmit={handleAssignTeacher} className="class-form">
        <label>Program</label>
        <select
          value={assignForm.programId}
          onChange={(e) =>
            setAssignForm({ ...assignForm, programId: e.target.value })
          }
          required
        >
          <option value="">Select Program</option>
          {programs && programs.length > 0 ? (
            programs.map((p) => (
              <option key={p._id} value={p._id}>
                {p.name}
              </option>
            ))
          ) : (
            <option value="" disabled>
              No programs available
            </option>
          )}
        </select>

        <label>Cohort</label>
        <select
          value={assignForm.cohortId}
          onChange={(e) =>
            setAssignForm({ ...assignForm, cohortId: e.target.value })
          }
          required
        >
          <option value="">Select Cohort</option>
          {activeCohorts && activeCohorts.length > 0 ? (
            activeCohorts.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name || c.cohortCode}
              </option>
            ))
          ) : (
            <option value="" disabled>
              No cohorts available
            </option>
          )}
        </select>

        <label>Subject</label>
        <select
          value={assignForm.subjectId}
          onChange={(e) =>
            setAssignForm({ ...assignForm, subjectId: e.target.value })
          }
          required
        >
          <option value="">Select Subject</option>
          {subjects && subjects.length > 0 ? (
            subjects.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name}
                {s.semester ? ` (Sem ${s.semester})` : ""}
              </option>
            ))
          ) : (
            <option value="" disabled>
              No subjects available
            </option>
          )}
        </select>

        <label>Teacher</label>
        <select
          value={assignForm.teacherId}
          onChange={(e) =>
            setAssignForm({ ...assignForm, teacherId: e.target.value })
          }
          required
        >
          <option value="">Select Teacher</option>
          {teachers && teachers.length > 0 ? (
            teachers.map((t) => (
              <option key={t._id} value={t._id}>
                {t.fullName}
              </option>
            ))
          ) : (
            <option value="" disabled>
              No teachers available
            </option>
          )}
        </select>

        <button
          className="btn-primary"
          type="submit"
          disabled={
            !assignForm.cohortId ||
            !assignForm.subjectId ||
            !assignForm.teacherId
          }
        >
          Assign Teacher
        </button>
      </form>

      <h3 style={{ marginTop: "30px" }}>Assigned Subjects</h3>

      {visibleAssignments && visibleAssignments.length > 0 ? (
        <table className="approval-table">
          <thead>
            <tr>
              <th>Subject</th>
              <th>Teacher</th>
              <th>Cohort</th>
            </tr>
          </thead>
          <tbody>
            {visibleAssignments.map((a) => (
              <tr key={a._id}>
                <td><span className="hide-for-desktop">Subject: </span>{a.subjectId?.name || "N/A"}</td>
                <td><span className="hide-for-desktop">Teacher: </span>{a.teacherId?.fullName || "N/A"}</td>
                <td><span className="hide-for-desktop">Cohort: </span>{a.cohortId?.cohortCode || "N/A"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No assignments found.</p>
      )}
    </div>
  );
};

  // Create Teacher Modal (unchanged)
  const AddTeacherModal = useMemo(() => {
    const ModalComponent = React.memo(
      ({ show, onClose, onSubmit, onChange, formData, isEdit = false }) => {
        const handleInputChange = useCallback(
          (e) => {
            onChange(e);
          },
          [onChange],
        );

        const handleSubmit = useCallback(
          (e) => {
            e.preventDefault();
            onSubmit(e);
          },
          [onSubmit],
        );

        const handleClose = useCallback(() => {
          onClose();
        }, [onClose]);

        if (!show) return null;

        return (
          <div className="modal-overlay">
            <div className="modal-box">
              <h2>{isEdit ? "Edit Teacher" : "Create Teacher"}</h2>

              <form onSubmit={handleSubmit} className="modal-form">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  required
                  onChange={handleInputChange}
                  value={formData.fullName || ""}
                />

                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  required
                  onChange={handleInputChange}
                  value={formData.email || ""}
                  disabled={isEdit}
                />

                <label>Contact *</label>
                <input
                  type="text"
                  name="contact"
                  required
                  onChange={handleInputChange}
                  value={formData.contact || ""}
                />

                {!isEdit && (
                  <>
                    <label>Password *</label>
                    <input
                      type="password"
                      name="password"
                      required
                      onChange={handleInputChange}
                      value={formData.password || ""}
                    />
                  </>
                )}

                <label>Course *</label>
                <select
                  name="course"
                  required
                  onChange={handleInputChange}
                  value={formData.course || ""}
                >
                  <option value="">Select Course</option>
                  <option value="NTT">NTT (Nursery Teacher Training)</option>
                  <option value="MTT">MTT (Middle Teacher Training)</option>
                  <option value="PTT">PTT (Primary Teacher Training)</option>
                </select>

                <label>Subject Specialization</label>
                <input
                  type="text"
                  name="subject"
                  onChange={handleInputChange}
                  value={formData.subject || ""}
                  placeholder="e.g., Mathematics, Science, English"
                />

                <label>Qualifications</label>
                <input
                  type="text"
                  name="qualifications"
                  onChange={handleInputChange}
                  value={formData.qualifications || ""}
                />

                <label>Experience</label>
                <input
                  type="text"
                  name="experience"
                  onChange={handleInputChange}
                  value={formData.experience || ""}
                />

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {isEdit ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      },
    );

    ModalComponent.displayName = "AddTeacherModal";
    return ModalComponent;
  }, []);

  // Edit Student Modal (unchanged)
  const EditStudentModal = useMemo(() => {
    const ModalComponent = React.memo(
      ({ show, onClose, onSubmit, onChange, formData, programs, cohorts }) => {
        const [activeCohorts, setActiveCohorts] = useState([]);

        // Filter cohorts based on selected program
        useEffect(() => {
          if (!formData.programId) {
            setActiveCohorts([]);
            return;
          }

          const filtered = cohorts.filter(
            (c) =>
              c.programId?._id === formData.programId ||
              c.programId === formData.programId,
          );
          setActiveCohorts(filtered);
        }, [formData.programId, cohorts]);

        const handleInputChange = useCallback(
          (e) => {
            onChange(e);
          },
          [onChange],
        );

        const handleSubmit = useCallback(
          (e) => {
            e.preventDefault();
            onSubmit(e);
          },
          [onSubmit],
        );

        const handleClose = useCallback(() => {
          onClose();
        }, [onClose]);

        if (!show) return null;

        return (
          <div className="modal-overlay">
            <div className="modal-box">
              <h2>Edit Student</h2>

              <form onSubmit={handleSubmit} className="modal-form">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  required
                  onChange={handleInputChange}
                  value={formData.fullName || ""}
                />

                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  required
                  onChange={handleInputChange}
                  value={formData.email || ""}
                  disabled
                />

                <label>Contact *</label>
                <input
                  type="text"
                  name="contact"
                  required
                  onChange={handleInputChange}
                  value={formData.contact || ""}
                />

                <label>Enrollment ID</label>
                <input
                  type="text"
                  name="enrollmentId"
                  onChange={handleInputChange}
                  value={formData.enrollmentId || ""}
                />

                <label>Program *</label>
                <select
                  name="programId"
                  required
                  onChange={handleInputChange}
                  value={formData.programId || ""}
                >
                  <option value="">Select Program</option>
                  {programs.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.name}
                    </option>
                  ))}
                </select>

                <label>Cohort/Batch</label>
                <select
                  name="cohortId"
                  onChange={handleInputChange}
                  value={formData.cohortId || ""}
                >
                  <option value="">Select Cohort</option>
                  {activeCohorts.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.cohortCode} {c.name ? `(${c.name})` : ""}
                    </option>
                  ))}
                </select>

                <label>Status *</label>
                <select
                  name="status"
                  required
                  onChange={handleInputChange}
                  value={formData.status || "pending"}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="active">Active</option>
                </select>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Update Student
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      },
    );

    ModalComponent.displayName = "EditStudentModal";
    return ModalComponent;
  }, []);

  // Google Classroom authentication handler
  const handleGoogleAuth = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/google-classroom/auth`;
  };

  // Add this with other modal components
const LeaveDetailsModal = useMemo(() => {
  const ModalComponent = React.memo(({ 
    show, 
    onClose, 
    leave,
    handleLeaveReview
  }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [reviewComments, setReviewComments] = useState("");
    const [reviewStatus, setReviewStatus] = useState("approved");
    
    const handleReviewSubmit = async () => {
      if (!reviewStatus) {
        toast.error("Please select a status");
        return;
      }
      
      setIsProcessing(true);
      try {
        await handleLeaveReview(leave._id, reviewStatus, reviewComments);
        onClose();
      } catch (error) {
        console.error("Error reviewing leave:", error);
      } finally {
        setIsProcessing(false);
      }
    };
    
    const getLeaveTypeLabel = (type) => {
      switch(type) {
        case 'medical': return '🏥 Medical Leave';
        case 'personal': return '👤 Personal Leave';
        case 'emergency': return '🚨 Emergency Leave';
        case 'academic': return '📚 Academic Leave';
        case 'other': return '📝 Other';
        default: return type;
      }
    };
    
    const getStatusLabel = (status) => {
      switch(status) {
        case 'pending': return '⏳ Pending';
        case 'approved': return '✅ Approved';
        case 'rejected': return '❌ Rejected';
        case 'cancelled': return '🚫 Cancelled';
        default: return status;
      }
    };
    
    if (!show || !leave) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal-box wide-modal">
          <div className="modal-header">
            <h2>Leave Application Details</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          
          <div className="leave-details-modal">
            {/* Student Information */}
            <div className="detail-section">
              <h4>Student Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <strong>Student Name:</strong> {leave.studentName}
                </div>
                <div className="detail-item">
                  <strong>Enrollment ID:</strong> {leave.enrollmentId}
                </div>
                <div className="detail-item">
                  <strong>Program:</strong> {leave.programId?.name || 'N/A'}
                </div>
                <div className="detail-item">
                  <strong>Cohort:</strong> {leave.cohortId?.cohortCode || 'N/A'}
                </div>
              </div>
            </div>
            
            {/* Leave Details */}
            <div className="detail-section">
              <h4>Leave Details</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <strong>Leave Type:</strong> {getLeaveTypeLabel(leave.leaveType)}
                </div>
                <div className="detail-item">
                  <strong>Start Date:</strong> {new Date(leave.startDate).toLocaleDateString()}
                </div>
                <div className="detail-item">
                  <strong>End Date:</strong> {new Date(leave.endDate).toLocaleDateString()}
                </div>
                <div className="detail-item">
                  <strong>Total Days:</strong> {leave.totalDays} day(s)
                </div>
                <div className="detail-item">
                  <strong>Current Status:</strong> 
                  <span className={`status-badge ${leave.status}`}>
                    {getStatusLabel(leave.status)}
                  </span>
                </div>
              </div>
              
              <div className="detail-item-full">
                <strong>Reason for Leave:</strong>
                <div className="reason-text">{leave.reason}</div>
              </div>
              
              {leave.supportingDocument && (
                <div className="detail-item-full">
                  <strong>Supporting Document:</strong>
                  <a 
                    href={`${process.env.REACT_APP_IMG_URL}uploads/leaves/${leave.supportingDocument}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="doc-link"
                  >
                    <FaFileAlt /> View Document
                  </a>
                </div>
              )}
            </div>
            
            {/* Application Timeline */}
            <div className="detail-section">
              <h4>Application Timeline</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <strong>Applied On:</strong> {new Date(leave.createdAt).toLocaleString()}
                </div>
                {leave.reviewedAt && (
                  <div className="detail-item">
                    <strong>Reviewed On:</strong> {new Date(leave.reviewedAt).toLocaleString()}
                  </div>
                )}
                {leave.reviewedBy && (
                  <div className="detail-item">
                    <strong>Reviewed By:</strong> {leave.reviewedBy?.fullName || 'Admin'}
                  </div>
                )}
                {leave.reviewComments && (
                  <div className="detail-item-full">
                    <strong>Review Comments:</strong>
                    <div className="comments-text">{leave.reviewComments}</div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Review Section (only for pending leaves) */}
            {leave.status === 'pending' && (
              <div className="review-section">
                <h4>Review Leave Application</h4>
                <div className="review-form">
                  <div className="form-group">
                    <label>Decision *</label>
                    <select 
                      value={reviewStatus}
                      onChange={(e) => setReviewStatus(e.target.value)}
                      className="status-select"
                    >
                      <option value="approved">✅ Approve Leave</option>
                      <option value="rejected">❌ Reject Leave</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Comments (Optional)</label>
                    <textarea 
                      value={reviewComments}
                      onChange={(e) => setReviewComments(e.target.value)}
                      placeholder="Add comments for the student..."
                      rows="3"
                    />
                  </div>
                  
                  <div className="review-actions">
                    <button 
                      className="btn-secondary"
                      onClick={onClose}
                      disabled={isProcessing}
                    >
                      Cancel
                    </button>
                    <button 
                      className={reviewStatus === 'approved' ? 'btn-approve' : 'btn-reject'}
                      onClick={handleReviewSubmit}
                      disabled={isProcessing}
                    >
                      {isProcessing ? 'Processing...' : 
                       reviewStatus === 'approved' ? 'Approve Leave' : 'Reject Leave'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* For non-pending leaves */}
            {leave.status !== 'pending' && (
              <div className="review-info">
                <p>
                  This leave has been <strong>{leave.status}</strong>.
                  {leave.reviewComments && ` Comments: ${leave.reviewComments}`}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  });

  ModalComponent.displayName = "LeaveDetailsModal";
  return ModalComponent;
}, []);

// Add the handleLeaveReview function
const handleLeaveReview = async (leaveId, status, comments) => {
  try {
    const response = await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/leaves/admin/review/${leaveId}`,
      {
        status,
        reviewComments: comments
      }
    );
    
    if (response.data.success) {
      toast.success(`Leave application ${status} successfully`);
      fetchLeaveApplications();
      return true;
    }
  } catch (error) {
    console.error("Error reviewing leave:", error);
    toast.success(error.response?.data?.message || "Failed to review leave");
    return false;
  }
};

  const fetchGoogleCourses = async () => {
    setLoadingGoogle(true);
    try {
      console.log("Fetching Google courses...");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/google-classroom/courses`,
      );
      console.log("Courses response:", response.data);

      // Check response structure
      if (response.data && response.data.courses) {
        setGoogleCourses(response.data.courses);
      } else if (Array.isArray(response.data)) {
        setGoogleCourses(response.data);
      } else {
        console.warn("Unexpected response format:", response.data);
        setGoogleCourses([]);
      }

      toast.success("Courses fetched successfully!");
    } catch (error) {
      console.error(
        "Error fetching courses:",
        error.response?.data || error.message,
      );
      toast.error("Failed to fetch Google Classroom courses");
      setGoogleCourses([]);
    } finally {
      setLoadingGoogle(false);
    }
  };

  const fetchCourseDetails = async (courseId) => {
    setLoadingGoogle(true);
    try {
      console.log("Fetching details for course:", courseId);
      console.log(
        "Current authenticated user from localStorage:",
        localStorage.getItem("googleUser"),
      );

      // Add withCredentials to ensure session is sent
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/google-classroom/courses/${courseId}`,
        {
          withCredentials: true,
        },
      );

      console.log("Course details response:", response.data);
      setCourseDetails(response.data);
      setSelectedCourse(courseId);
    } catch (error) {
      console.error(
        "Error fetching course details:",
        error.response?.data || error.message,
      );
      console.error("Full error:", error);

      // Check if it's an authentication error
      if (error.response?.status === 401) {
        toast.error("Authentication expired. Please reconnect to Google.");
        setGoogleAuthenticated(false);
        localStorage.removeItem("googleUser");
      } else {
        toast.error("Failed to fetch course details");
      }
    } finally {
      setLoadingGoogle(false);
    }
  };

  const handleGoogleReAuth = () => {
  // Force fresh Google account selection
  window.location.href =
    `${process.env.REACT_APP_BACKEND_URL}/google-classroom/auth?prompt=select_account`;
};




  // Add these modals to your return statement
  return (
    <>
      <div className="admin-dashboard-wrapper">
       

        <div className="admin-container">
         <div className={`admin-sidebar ${sidebarOpen ? "open" : ""}`}>

            <h2>Admin Panel</h2>
   
<button 
  className="sidebar-close-btn"
  onClick={() => setSidebarOpen(false)}
>
  ✕
</button>


         <ul className="sidebar-menu">
  <li
    className={activeTab === "dashboard" ? "active" : ""}
    onClick={() => {
      setActiveTab("dashboard");
      setSidebarOpen(false);
    }}
  >
    <FaUsers /> Dashboard
  </li>

  <li
    className={activeTab === "students" ? "active" : ""}
    onClick={() => {
      setActiveTab("students");
      setSidebarOpen(false);
    }}
  >
    <FaUsers /> Students
  </li>

  <li
    className={activeTab === "admissionDetails" ? "active" : ""}
    onClick={() => {
      setActiveTab("admissionDetails");
      setSidebarOpen(false);
    }}
  >
    <FaFileAlt /> Admission Forms
  </li>

  <li
    className={activeTab === "teachers" ? "active" : ""}
    onClick={() => {
      setActiveTab("teachers");
      setSidebarOpen(false);
    }}
  >
    <FaChalkboardTeacher /> Teachers
  </li>

  <li
    className={activeTab === "schedule" ? "active" : ""}
    onClick={() => {
      setActiveTab("schedule");
      setSidebarOpen(false);
    }}
  >
    <FaCalendarAlt /> Assign Subject
  </li>

  <li
    className={activeTab === "cohorts" ? "active" : ""}
    onClick={() => {
      setActiveTab("cohorts");
      setSidebarOpen(false);
    }}
  >
    <FaClipboardList /> Batch
  </li>

  <li
    className={activeTab === "batchDetails" ? "active" : ""}
    onClick={() => {
      setActiveTab("batchDetails");
      setSidebarOpen(false);
    }}
  >
    <FaClipboardList /> Batch Details
  </li>

  <li
    className={activeTab === "leaveApplications" ? "active" : ""}
    onClick={() => {
      setActiveTab("leaveApplications");
      setSidebarOpen(false);
    }}
  >
    <FaCalendarAlt /> Leave Applications
  </li>

  <li
    className={activeTab === "googleClassroom" ? "active" : ""}
    onClick={() => {
      setActiveTab("googleClassroom");
      setSidebarOpen(false);
    }}
  >
    <FaGoogle /> Google Classroom
  </li>
</ul>

          </div>

          <div className="admin-content">
            <div className="admin-header">
                                  <button 
  className="mobile-menu-btn"
  onClick={() => setSidebarOpen(true)}
>
  ☰
</button>
              <h1>Admin Dashboard</h1>
   
              <div className="header-actions">
                <span className="welcome-text">Welcome, Admin!</span>
              </div>
            </div>

            {activeTab === "dashboard" && renderDashboard()}
            {activeTab === "schedule" && renderSubjectTeacherAssignment()}
            {activeTab === "admissionDetails" && renderAdmissionDetails()}
            {activeTab === "students" && renderStudents()}
            {activeTab === "teachers" && renderTeachers()}
            {activeTab === "cohorts" && renderCohorts()}
            {activeTab === "batchDetails" && renderBatchDetails()}
            {activeTab === "googleClassroom" && renderGoogleClassroom()}
            
{activeTab === "leaveApplications" && renderLeaveApplications()}
          </div>
        </div>
      </div>

      <AddTeacherModal
        show={showTeacherModal}
        onClose={() => {
          setShowTeacherModal(false);
          setTeacherForm({});
        }}
        onSubmit={handleCreateTeacher}
        onChange={handleTeacherChange}
        formData={teacherForm}
      />

      <AddTeacherModal
        show={showEditTeacherModal}
        onClose={() => {
          setShowEditTeacherModal(false);
          setEditingTeacher(null);
          setTeacherForm({});
        }}
        onSubmit={handleEditTeacher}
        onChange={handleTeacherChange}
        formData={teacherForm}
        isEdit={true}
      />

      <EditStudentModal
        show={showEditStudentModal}
        onClose={() => {
          setShowEditStudentModal(false);
          setEditingStudent(null);
          setStudentForm({});
        }}
        onSubmit={handleEditStudent}
        onChange={handleStudentChange}
        formData={studentForm}
        programs={programs}
        cohorts={cohorts}
      />

      {showAddStudentModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h2>Add Student</h2>

            <form onSubmit={handleCreateStudent} className="modal-form">
              <label>Full Name *</label>
              <input
                required
                value={newStudentForm.fullName}
                onChange={(e) =>
                  setNewStudentForm({
                    ...newStudentForm,
                    fullName: e.target.value,
                  })
                }
              />

              <label>Father Name</label>
              <input
                value={newStudentForm.fatherName}
                onChange={(e) =>
                  setNewStudentForm({
                    ...newStudentForm,
                    fatherName: e.target.value,
                  })
                }
              />

              <label>Email *</label>
              <input
                type="email"
                required
                value={newStudentForm.email}
                onChange={(e) =>
                  setNewStudentForm({
                    ...newStudentForm,
                    email: e.target.value,
                  })
                }
              />

              <label>Password *</label>
              <input
                type="password"
                required
                value={newStudentForm.password}
                onChange={(e) =>
                  setNewStudentForm({
                    ...newStudentForm,
                    password: e.target.value,
                  })
                }
              />

              <label>Contact *</label>
              <input
                required
                value={newStudentForm.contact}
                onChange={(e) =>
                  setNewStudentForm({
                    ...newStudentForm,
                    contact: e.target.value,
                  })
                }
              />

              <label>Alternate Contact</label>
              <input
                value={newStudentForm.altContact}
                onChange={(e) =>
                  setNewStudentForm({
                    ...newStudentForm,
                    altContact: e.target.value,
                  })
                }
              />

              <label>Date of Birth</label>
              <input
                type="date"
                value={newStudentForm.dob}
                onChange={(e) =>
                  setNewStudentForm({ ...newStudentForm, dob: e.target.value })
                }
              />

              <label>Address</label>
              <textarea
                value={newStudentForm.address}
                onChange={(e) =>
                  setNewStudentForm({
                    ...newStudentForm,
                    address: e.target.value,
                  })
                }
              />

              <label>Program</label>
              <select
                required
                value={newStudentForm.programId}
                onChange={(e) =>
                  setNewStudentForm({
                    ...newStudentForm,
                    programId: e.target.value,
                  })
                }
              >
                <option value="">Select Program</option>
                {programs.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.name}
                  </option>
                ))}
              </select>

              <label>Cohort</label>
              <select
                value={newStudentForm.cohortId}
                onChange={(e) =>
                  setNewStudentForm({
                    ...newStudentForm,
                    cohortId: e.target.value,
                  })
                }
              >
                <option value="">Select Cohort</option>
                {activeCohorts.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.cohortCode}
                  </option>
                ))}
              </select>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowAddStudentModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Student
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    <AdmissionDetailsModal
  show={showAdmissionModal}
  onClose={() => {
    setShowAdmissionModal(false);
    setSelectedAdmission(null);
  }}
  admission={selectedAdmission}
  programs={programs}
  cohorts={cohorts}
  setActiveTab={setActiveTab} // Pass setActiveTab function
/>


<LeaveDetailsModal
  show={showLeaveModal}
  onClose={() => {
    setShowLeaveModal(false);
    setSelectedLeave(null);
  }}
  leave={selectedLeave}
  handleLeaveReview={handleLeaveReview}
/>
    </>

    
  );


  
};

export default AdminDashboard;
